'use client';

import { use, useState } from 'react';
import { useLibraryStore } from '@/stores/library-store';
import { useOpdsFeed } from '@/hooks/useOpdsFeed';
import { useHydrated } from '@/hooks/useHydrated';
import { Header } from '@/components/layout/Header';
import { NavigationFeed } from '@/components/catalog/NavigationFeed';
import { AcquisitionFeed } from '@/components/catalog/AcquisitionFeed';
import { FacetBar } from '@/components/catalog/FacetBar';
import { PaginationControls } from '@/components/catalog/PaginationControls';
import { SearchDialog } from '@/components/search/SearchDialog';
import { Spinner } from '@/components/ui/Spinner';
import { LibraryOfflineCard } from '@/components/feedback/LibraryOfflineCard';
import { isUpstreamDown } from '@/lib/utils/opds-errors';

export default function LibraryRootPage({
  params,
}: {
  params: Promise<{ libraryId: string }>;
}) {
  const { libraryId } = use(params);
  const hydrated = useHydrated();
  const library = useLibraryStore((s) => s.getLibrary(libraryId));
  const { feed, loading, error, reload } = useOpdsFeed(hydrated ? (library?.rootUrl ?? null) : null, hydrated ? library : undefined);
  const [searchOpen, setSearchOpen] = useState(false);

  if (!hydrated) {
    return (
      <>
        <Header title="" showBack />
        <main className="p-6"><Spinner className="py-16" /></main>
      </>
    );
  }

  if (!library) {
    return (
      <>
        <Header title="Библиотека не найдена" showBack />
        <main className="p-6 text-center text-neutral-500">
          Такой библиотеки нет. Возможно, её удалили.
        </main>
      </>
    );
  }

  return (
    <>
      <Header
        title={library.name}
        showBack
        actions={
          feed?.searchUrl ? (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Поиск
            </button>
          ) : undefined
        }
      />

      <main className="p-6">
        {loading && <Spinner className="py-16" />}
        {error && (
          isUpstreamDown(error) ? (
            <LibraryOfflineCard onRetry={reload} />
          ) : (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
              <p className="font-medium">Не удалось загрузить каталог</p>
              <p className="mt-1">{error}</p>
            </div>
          )
        )}

        {feed && (
          <>
            <FacetBar facets={feed.facets} library={library} />

            {feed.kind === 'navigation' ? (
              <NavigationFeed feed={feed} library={library} />
            ) : (
              <AcquisitionFeed feed={feed} library={library} feedUrl={library.rootUrl} />
            )}

            <PaginationControls feed={feed} library={library} />
          </>
        )}
      </main>

      {feed?.searchUrl && (
        <SearchDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          library={library}
          searchDescUrl={feed.searchUrl}
        />
      )}
    </>
  );
}
